import Loader from 'Components/Common/Loader';
import PreviewCardHeader from 'Components/Common/PreviewCardHeader';
import UserFormFields from 'Components/user/UserFormFields';
import UserPrivileges from 'Components/user/UserPrivileges';
import { useFormik } from 'formik';
import useDocumentTitle from 'hooks/useDocumentTitle';
import Error404 from 'pages/Error/Error404';
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, CardBody, CardFooter, Col, Container, Form, Row, Spinner } from 'reactstrap';
import { updateUser } from 'services/api/users';
import { fetchRoles } from 'slices/users/roles/thunk';
import { fetchUserData, fetchUserList } from 'slices/users/thunk';
import { AppDispatch, RootState } from 'store';
import { NewUser } from 'types/users';
import { getErrorMessage } from 'utils/helpers';
import * as Yup from "yup";
import { UserFormValues } from './Create';
import withAccessControl from 'hoc/withAccessControl';
import { PRIVILEGE } from 'constants/privilege';

const EditUser = () => {
    useDocumentTitle("Edit User");
    const selectedHub = useSelector((state: RootState) => state.auth.selectedHub);

    const { userId } = useParams();

    const dispatch: AppDispatch = useDispatch();

    const navigate = useNavigate();
    const user = useSelector((state: RootState) => state.users.items[userId!]?.user);

    const loading = useSelector((state: RootState) => state.users.itemLoading);
    const rolesLoading = useSelector((state: RootState) => state.roles.loading);
    const [updating, setUpdating] = useState(false);

    const initialValues: UserFormValues = {
        name: user?.name ?? '',
        email: user?.email ?? '',
        role: user?.role ?? null
    }

    const formFields = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,
        initialValues,
        validationSchema: Yup.object({
            name: Yup.string().required("User name field can't be empty"),
            email: Yup.string().required("User email field can't be empty").email("Please enter a valid email address"),
        }),
        onSubmit: async (values) => {
            try {
                const newUser: NewUser = {
                    ...values,
                    role: values.role?.slug ?? ''
                }
                setUpdating(true);
                await updateUser(selectedHub?.uuid!, user.uuid, newUser);
                toast.success("User updated successfully!");
                dispatch(fetchUserData(userId!))
                dispatch(fetchUserList({ page: 1, force: true }));
            } catch (error) {
                toast.error(getErrorMessage(error, 'Failed to update new user'));
            } finally {
                setUpdating(false)
            }
        }
    });

    useEffect(() => {
        dispatch(fetchRoles(false));
    }, [dispatch])

    useEffect(() => {
        if (userId)
            dispatch(fetchUserData(userId));
    }, [userId, dispatch])


    if (loading || rolesLoading) return <Loader />

    if (!loading && !user) return <Error404 />
    
    return (
        <div className="page-content">
            <Container fluid>
                <Form className='needs-validation' onSubmit={(e) => {
                    e.preventDefault();
                    formFields.handleSubmit();
                    return false;
                }}>
                    <Row>
                        <Col xl={6}>

                            <Card>
                                <PreviewCardHeader title="Edit User" />
                                <CardBody>
                                    <UserFormFields editMode={true} formFields={formFields} />
                                </CardBody>
                                <CardFooter>

                                    <div className="d-flex justify-content-between align-items-center">
                                         <button type='button' onClick={() => navigate('/user')} className="btn btn-outline-primary d-flex align-items-center">
                                            <i className='bx bx-left-arrow-alt fs-4'></i> Back
                                        </button>
                                        <button type='submit' disabled={updating} className="btn d-flex align-items-center btn-primary">
                                            {
                                                updating ? <Spinner size="sm" className='me-2'> Loading... </Spinner> : <React.Fragment>Save <i className='bx fs-4 bx-right-arrow-alt' ></i></React.Fragment>
                                            }
                                        </button>
                                    </div>
                                </CardFooter>
                            </Card>
                        </Col>
                        {
                            formFields.values.role &&
                            <Col xl={6}>
                                <Card>
                                    <PreviewCardHeader title="Privileges" />
                                    <CardBody>
                                        <UserPrivileges selectedRole={formFields.values.role} />
                                    </CardBody>
                                </Card>
                            </Col>
                        }
                    </Row>
                </Form>
            </Container>
        </div>
    )
}

export default withAccessControl(EditUser, PRIVILEGE.HUB.USER.EDIT)